<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  KeyRound,
  LogOut,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
} from 'lucide-vue-next'

import type { GuestAccount, GuestAccountCreate, GuestAccountUpdate } from '@/types/monitor'
import { formatLocalDateTime } from '@/utils/format'
import { validateGuestAccountForm } from '@/utils/guestAccountForm'

const props = defineProps<{
  accounts: GuestAccount[]
  busy: boolean
}>()

const emit = defineEmits<{
  refresh: []
  create: [account: GuestAccountCreate]
  update: [id: string, account: GuestAccountUpdate]
  delete: [id: string]
  revokeSessions: [id: string]
  revokeAllSessions: []
}>()

const { t } = useI18n()
const editorVisible = ref(false)
const deleteVisible = ref(false)
const editingId = ref<string | null>(null)
const deletingAccount = ref<GuestAccount | null>(null)
const submitAttempted = ref(false)
const form = reactive({ username: '', password: '', enabled: true })

const enabledCount = computed(() => props.accounts.filter((account) => account.enabled).length)
const activeSessions = computed(() =>
  props.accounts.reduce((total, account) => total + account.active_sessions, 0),
)
const editorTitle = computed(() =>
  editingId.value ? t('guestAccounts.editTitle') : t('guestAccounts.createTitle'),
)
const validation = computed(() => validateGuestAccountForm(form.username, form.password, Boolean(editingId.value)))
const usernameInvalid = computed(() => validation.value.usernameInvalid)
const passwordInvalid = computed(() => validation.value.passwordInvalid)

function openCreate(): void {
  editingId.value = null
  form.username = ''
  form.password = ''
  form.enabled = true
  submitAttempted.value = false
  editorVisible.value = true
}

function openEdit(account: GuestAccount): void {
  editingId.value = account.id
  form.username = account.username
  form.password = ''
  form.enabled = account.enabled
  submitAttempted.value = false
  editorVisible.value = true
}

function submit(): void {
  submitAttempted.value = true
  if (usernameInvalid.value || passwordInvalid.value || props.busy) return

  const username = form.username.trim()
  if (editingId.value) {
    emit('update', editingId.value, {
      username,
      enabled: form.enabled,
      password: form.password || null,
    })
  } else {
    emit('create', { username, password: form.password, enabled: form.enabled })
  }
  editorVisible.value = false
  submitAttempted.value = false
  form.password = ''
}

function closeEditor(): void {
  editorVisible.value = false
  submitAttempted.value = false
  form.password = ''
}

function askDelete(account: GuestAccount): void {
  deletingAccount.value = account
  deleteVisible.value = true
}

function confirmDelete(): void {
  if (!deletingAccount.value) return
  emit('delete', deletingAccount.value.id)
  deleteVisible.value = false
  deletingAccount.value = null
}

function formatTime(value: string): string {
  return formatLocalDateTime(value)
}
</script>

<template>
  <section class="tm-view tm-guests-view">
    <header class="tm-page-header">
      <div>
        <h1>{{ t('guestAccounts.title') }}</h1>
        <p>{{ t('guestAccounts.description') }}</p>
      </div>
      <div class="tm-page-actions">
        <button class="tm-button" :disabled="busy" @click="emit('refresh')">
          <RefreshCw :size="16" />{{ t('common.refresh') }}
        </button>
        <button class="tm-button tm-button--primary" :disabled="busy" @click="openCreate">
          <Plus :size="17" />{{ t('guestAccounts.add') }}
        </button>
      </div>
    </header>

    <div class="tm-guest-summary">
      <div>
        <span class="tm-guest-summary-icon"><UsersRound :size="17" /></span>
        <span>{{ t('guestAccounts.total') }}</span>
        <strong>{{ accounts.length }}</strong>
      </div>
      <div>
        <span class="tm-guest-summary-icon"><ShieldCheck :size="17" /></span>
        <span>{{ t('guestAccounts.enabledCount') }}</span>
        <strong>{{ enabledCount }}</strong>
      </div>
      <div>
        <span class="tm-guest-summary-icon"><UserRound :size="17" /></span>
        <span>{{ t('guestAccounts.activeSessions') }}</span>
        <strong>{{ activeSessions }}</strong>
      </div>
      <button
        class="tm-guest-revoke-all"
        :disabled="busy || activeSessions === 0"
        @click="emit('revokeAllSessions')"
      >
        <LogOut :size="15" />{{ t('guestAccounts.revokeAll') }}
      </button>
    </div>

    <article class="tm-card tm-guest-table-card">
      <div class="tm-guest-table-head">
        <span>{{ t('guestAccounts.account') }}</span>
        <span>{{ t('guestAccounts.status') }}</span>
        <span>{{ t('guestAccounts.sessions') }}</span>
        <span>{{ t('guestAccounts.updatedAt') }}</span>
        <span>{{ t('guestAccounts.actions') }}</span>
      </div>

      <div v-if="accounts.length === 0" class="tm-guest-empty">
        <span><UsersRound :size="25" /></span>
        <strong>{{ t('guestAccounts.emptyTitle') }}</strong>
        <p>{{ t('guestAccounts.emptyHint') }}</p>
        <button class="tm-button tm-button--primary" @click="openCreate">
          <Plus :size="16" />{{ t('guestAccounts.add') }}
        </button>
      </div>

      <div v-else class="tm-guest-table-body">
        <div v-for="account in accounts" :key="account.id" class="tm-guest-row">
          <div class="tm-guest-identity">
            <span class="tm-guest-avatar"><UserRound :size="17" /></span>
            <div>
              <strong>{{ account.username }}</strong>
              <small><KeyRound :size="12" />{{ t('guestAccounts.passwordProtected') }}</small>
            </div>
          </div>
          <div>
            <span :class="['tm-guest-badge', account.enabled ? 'is-enabled' : 'is-disabled']">
              <i></i>{{ account.enabled ? t('guestAccounts.enabled') : t('guestAccounts.disabled') }}
            </span>
          </div>
          <div class="tm-guest-session-count">
            <strong>{{ account.active_sessions }}</strong>
            <small>{{ t('guestAccounts.readOnly') }}</small>
          </div>
          <div class="tm-guest-updated">
            <strong>{{ formatTime(account.updated_at) }}</strong>
            <small>{{ account.id.slice(0, 8) }}</small>
          </div>
          <div class="tm-guest-row-actions">
            <button :title="t('guestAccounts.edit')" :disabled="busy" @click="openEdit(account)">
              <Pencil :size="15" />
            </button>
            <button
              :title="t('guestAccounts.revokeSessions')"
              :disabled="busy || account.active_sessions === 0"
              @click="emit('revokeSessions', account.id)"
            >
              <LogOut :size="15" />
            </button>
            <button class="danger" :title="t('guestAccounts.delete')" :disabled="busy" @click="askDelete(account)">
              <Trash2 :size="15" />
            </button>
          </div>
        </div>
      </div>
    </article>

    <div class="tm-guest-security-note">
      <ShieldCheck :size="17" />
      <div><strong>{{ t('guestAccounts.securityTitle') }}</strong><span>{{ t('guestAccounts.securityHint') }}</span></div>
    </div>

    <el-dialog
      v-model="editorVisible"
      :title="editorTitle"
      width="min(520px, 92vw)"
      class="tm-guest-dialog"
      :close-on-click-modal="false"
    >
      <div class="tm-guest-dialog-intro">
        <span><ShieldCheck :size="17" /></span>
        <div>
          <strong>{{ t('guestAccounts.editorHintTitle') }}</strong>
          <small>{{ t('guestAccounts.editorHint') }}</small>
        </div>
      </div>
      <div class="tm-guest-form">
        <label :class="['tm-field', 'tm-field--stacked', { 'has-error': submitAttempted && usernameInvalid }]">
          <span><UserRound :size="15" />{{ t('guestAccounts.username') }}</span>
          <el-input
            v-model="form.username"
            :class="{ 'is-error': submitAttempted && usernameInvalid }"
            maxlength="64"
            autocomplete="off"
            :placeholder="t('guestAccounts.usernamePlaceholder')"
            @keyup.enter="submit"
          />
          <small v-if="submitAttempted && usernameInvalid" class="tm-field-error">{{ t('guestAccounts.usernameError') }}</small>
          <small v-else>{{ t('guestAccounts.usernameHint') }}</small>
        </label>
        <label :class="['tm-field', 'tm-field--stacked', { 'has-error': submitAttempted && passwordInvalid }]">
          <span><KeyRound :size="15" />{{ editingId ? t('guestAccounts.newPassword') : t('guestAccounts.password') }}</span>
          <el-input
            v-model="form.password"
            :class="{ 'is-error': submitAttempted && passwordInvalid }"
            type="password"
            show-password
            maxlength="128"
            autocomplete="new-password"
            :placeholder="editingId ? t('guestAccounts.passwordKeepPlaceholder') : t('guestAccounts.passwordPlaceholder')"
            @keyup.enter="submit"
          />
          <small v-if="submitAttempted && passwordInvalid" class="tm-field-error">{{ t('guestAccounts.passwordError') }}</small>
          <small v-else>{{ editingId ? t('guestAccounts.passwordEditHint') : t('guestAccounts.passwordHint') }}</small>
        </label>
        <div class="tm-guest-enabled-row">
          <div><strong>{{ t('guestAccounts.enableLogin') }}</strong><small>{{ t('guestAccounts.enableLoginHint') }}</small></div>
          <el-switch v-model="form.enabled" />
        </div>
      </div>
      <template #footer>
        <div class="tm-guest-dialog-actions">
          <button class="tm-button" :disabled="busy" @click="closeEditor">{{ t('common.cancel') }}</button>
          <button class="tm-button tm-button--primary" :disabled="busy" @click="submit">
          {{ editingId ? t('common.save') : t('guestAccounts.create') }}
          </button>
        </div>
      </template>
    </el-dialog>

    <el-dialog
      v-model="deleteVisible"
      :title="t('guestAccounts.deleteTitle')"
      width="min(440px, 92vw)"
      class="tm-guest-dialog"
    >
      <p class="tm-guest-delete-copy">
        {{ t('guestAccounts.deleteHint', { username: deletingAccount?.username ?? '' }) }}
      </p>
      <template #footer>
        <button class="tm-button" :disabled="busy" @click="deleteVisible = false">{{ t('common.cancel') }}</button>
        <button class="tm-button tm-button--danger" :disabled="busy" @click="confirmDelete">
          <Trash2 :size="15" />{{ t('guestAccounts.confirmDelete') }}
        </button>
      </template>
    </el-dialog>
  </section>
</template>
